/**
 * Núcleo genérico de EXCLUSÃO MÚTUA por `ExclusionGroupCode`, compartilhado
 * entre produtos (Capital Global e PME).
 *
 * Diferente do mecanismo de coberturas do Capital Global (`RelationType`, que
 * só relaciona cobertura↔cobertura), este agrupa itens de QUALQUER tipo: uma
 * cobertura pode ser exclusiva com um benefício. Por isso o núcleo trabalha
 * sobre "membros" abstratos — cada produto monta a lista a partir do seu
 * próprio estado de seleção e roteia os conflitos para as suas telas.
 *
 * Regra: itens SELECIONADOS que compartilham o mesmo `groupCode` não podem
 * coexistir. Com N (≥2) selecionados no mesmo grupo, cada um acusa os outros —
 * a mensagem some sozinha quando o corretor desliga um deles.
 */

/** Um item selecionável que participa de um grupo de exclusão. */
export interface ExclusionMember<TRef> {
  /** Identidade opaca do item, devolvida no conflito para a tela roteá-lo. */
  ref: TRef;
  /** Nome exibido ao corretor na mensagem. */
  title: string;
  /**
   * Valor do `ExclusionGroupCode`. Vazio/ausente = não participa. Pode ser uma
   * LISTA separada por vírgulas (ex.:
   * `ASSISTENCIA_FUNERAL,AFASTAMENTO,ACB_GROUP`) quando o item pertence a mais
   * de um grupo — o membro participa de cada um deles.
   */
  groupCode?: string | null;
  /** `true` quando o corretor selecionou este item. */
  selected: boolean;
  /**
   * Identidade do membro no namespace de códigos do produto (código da
   * cobertura). Só é usada para casar com `allowedWith` do outro lado.
   */
  key?: string;
  /**
   * Keys com quem este membro PODE coexistir mesmo dividindo o grupo.
   * Dependência declarada (`RelationType=1`) vence a exclusão para o par
   * pai↔filho: o catálogo do Capital Global agrupa cada Assistência Funeral
   * com o Auxílio pai no MESMO grupo (`ASSISTENCIA_AUXILIO_FUNERAL`) — sem a
   * isenção, a dependência exige o par junto e a exclusão o proíbe, e a
   * cobertura fica impossível de contratar.
   */
  allowedWith?: string[];
}

export interface ExclusionConflict<TRef> {
  ref: TRef;
  /** Nomes dos demais itens do grupo com que este colide. */
  clashesWith: string[];
  message: string;
}

/**
 * Conflitos de exclusão para o estado atual. Puro: mesma entrada → mesma saída,
 * sem I/O. É a fonte ÚNICA das mensagens — tela (tempo real) e schema (bloqueio
 * do submit) devem ler daqui para não divergirem no texto.
 */
export function findExclusionGroupConflicts<TRef>(
  members: ExclusionMember<TRef>[]
): ExclusionConflict<TRef>[] {
  // Agrupa os SELECIONADOS com groupCode não-vazio. String vazia é ignorada de
  // propósito: há itens no produto com o campo em branco, e juntá-los num grupo
  // "" os tornaria mutuamente exclusivos sem nenhuma relação real. Um membro
  // com lista de códigos entra em cada grupo da lista.
  const byGroup = new Map<string, ExclusionMember<TRef>[]>();
  for (const member of members) {
    if (!member.selected) continue;
    for (const code of splitExclusionGroupCodes(member.groupCode)) {
      const bucket = byGroup.get(code);
      if (bucket) bucket.push(member);
      else byGroup.set(code, [member]);
    }
  }

  // Acumula por membro: um par pode dividir MAIS de um grupo (códigos em
  // lista) e o colega não deve repetir na mensagem.
  const clashesByMember = new Map<ExclusionMember<TRef>, Set<string>>();
  for (const group of byGroup.values()) {
    if (group.length < 2) continue;
    for (const member of group) {
      for (const other of group) {
        if (other === member || isPairAllowed(member, other)) continue;
        const titles = clashesByMember.get(member) ?? new Set<string>();
        titles.add(other.title);
        clashesByMember.set(member, titles);
      }
    }
  }

  const conflicts: ExclusionConflict<TRef>[] = [];
  for (const [member, titles] of clashesByMember) {
    const clashesWith = [...titles];
    conflicts.push({
      ref: member.ref,
      clashesWith,
      message: buildExclusionMessage(member.title, clashesWith),
    });
  }
  return conflicts;
}

/**
 * Códigos de grupo de um `ExclusionGroupCode`, que pode ser um código único ou
 * uma lista separada por vírgulas. Entradas vazias/em branco são descartadas.
 */
export function splitExclusionGroupCodes(
  groupCode: string | null | undefined
): string[] {
  if (!groupCode) return [];
  return groupCode
    .split(',')
    .map(code => code.trim())
    .filter(Boolean);
}

/** Par com coexistência permitida (`allowedWith`), em qualquer direção. */
function isPairAllowed<TRef>(
  a: ExclusionMember<TRef>,
  b: ExclusionMember<TRef>
): boolean {
  return (
    Boolean(a.key && b.allowedWith?.includes(a.key)) ||
    Boolean(b.key && a.allowedWith?.includes(b.key))
  );
}

/** Texto padrão do conflito — mesmo tom das mensagens de relação de cobertura. */
export function buildExclusionMessage(
  title: string,
  clashesWith: string[]
): string {
  return `É proibida a contratação de ${title} junto com ${clashesWith.join(', ')}.`;
}
